import React, { useRef, useEffect, useState } from 'react';
import { useDesignMode } from '../../contexts/DesignContext';
import {
  OnboardingHero,
  OnboardingWhatIs,
  OnboardingWorkflowSection,
  OnboardingDemoSection,
  OnboardingNextSection,
  OnboardingCtaButtons,
} from './OnboardingSections';

export type WelcomeOnboardingContentProps = {
  trackName: string;
  trackArtist: string;
  sharedTabLabel: string;
  demoLoading?: boolean;
  demoUnavailable?: boolean;
  onSkip: () => void;
  onDemo: () => void;
  onCreate: () => void;
};

export const WelcomeOnboardingMobile: React.FC<WelcomeOnboardingContentProps> = ({
  trackName,
  trackArtist,
  sharedTabLabel,
  demoLoading,
  demoUnavailable,
  onSkip,
  onDemo,
  onCreate,
}) => {
  const { isSketchy } = useDesignMode();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setScrollPct(max > 0 ? Math.round((el.scrollTop / max) * 100) : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="relative max-w-lg mx-auto w-full -mx-4 sm:mx-0">
      <div
        className="absolute right-0 top-4 bottom-48 w-1.5 z-10 pointer-events-none lg:hidden"
        aria-hidden
      >
        <div className="h-full rounded-full bg-[var(--color-hover)] overflow-hidden">
          <div
            className="w-full rounded-full bg-[var(--brand-from)] opacity-70 transition-all duration-150"
            style={{ height: `${Math.max(scrollPct, 8)}%` }}
          />
        </div>
      </div>

      <div
        ref={scrollRef}
        className="overflow-y-auto max-h-[calc(100vh-4rem)] px-4 py-2 pb-[calc(12rem+env(safe-area-inset-bottom))] pr-6 space-y-6 lg:hidden"
      >
        <OnboardingHero onSkip={onSkip} />
        <OnboardingWhatIs />
        <OnboardingWorkflowSection />
        <OnboardingDemoSection
          trackName={trackName}
          trackArtist={trackArtist}
          sharedTabLabel={sharedTabLabel}
          demoLoading={demoLoading}
          demoUnavailable={demoUnavailable}
        />
        <OnboardingNextSection />
      </div>

      <footer
        className={`fixed bottom-0 left-0 right-0 max-w-lg mx-auto px-4 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] border-t border-[var(--color-border)] backdrop-blur-sm z-20 lg:hidden ${
          isSketchy
            ? 'border-dashed nav-bar-sketchy bg-[color-mix(in_srgb,var(--color-bg)_95%,transparent)]'
            : 'bg-[var(--color-surface)]/95'
        }`}
      >
        <OnboardingCtaButtons
          onDemo={onDemo}
          onCreate={onCreate}
          onSkip={onSkip}
          demoLoading={demoLoading}
          demoUnavailable={demoUnavailable}
          layout="stack"
        />
      </footer>
    </div>
  );
};
